import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as React from "react";
import * as yup from "yup";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
  getCountries,
  IOrganizationAdd,
  OrganizationEnum,
} from "utils/api.interfaces";

import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import { useNavigate, useParams } from "react-router-dom";
import { IUsers } from "utils/api.interfaces";
import { Transition } from "utils/common";
import AddOrganizationAdmin from "../components/AddOrganizationAdmin";
import AddOrganizationOwner from "../components/AddOrganizationOwner";
import AutocompleteDebounce from "../components/AutocompleteDebounce";
import { ErrorMessageSlide } from "../components/ErrorMessageSlide";
import GooglePlacesAutocomplete from "../components/GooglePlacesAutocomplete";
import { ParsedAddress } from "../types/googlePlaces";
import UploadImage from "../components/UploadImage";
import { useLeagues } from "../hooks/useLeague";
import {
  postOrganizationImage,
  putOrganization,
  useOrganization,
} from "../hooks/useOrganization";
import { addAdminToOrganization, setOrganizationOwner } from "../hooks/useOrganizations";

export default function OrganizationEdit() {
  const { organizationId } = useParams();
  const { dataOrganization } = useOrganization(organizationId);

  const validationSchema = yup.object({
    name: yup.string().required("Name is required"),
  });

  const initOrganization: IOrganizationAdd = {
    id: dataOrganization?.id || "",
    name: dataOrganization?.name || "",
    shortName: dataOrganization?.shortName || "",
    logoUrl: dataOrganization?.logoUrl || "",
    isGhost: dataOrganization?.isGhost || false,
    leagueId: dataOrganization?.leagueId || "",
    type: dataOrganization?.type || OrganizationEnum.Club,
    sportType: dataOrganization?.sportType || "Football",
    countryCode: dataOrganization?.countryCode?.toUpperCase() || "NL",
    address: {
      line1: dataOrganization?.address?.line1 || "",
      line2: dataOrganization?.address?.line2 || "",
      zipCode: dataOrganization?.address?.zipCode || "",
      city: dataOrganization?.address?.city || "",
      region: dataOrganization?.address?.region || "",
    },
  };

  const [acceptedFiles, setAcceptedFiles] = React.useState<File[]>([]);
  const navigate = useNavigate();
  const [showSlideError, setShowSlideError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState<Boolean>(false);
  const [newOrganization, setNewOrganization] = React.useState<
    string | undefined
  >(undefined);

  const [leaguesFilter, setLeaguesFilter] = React.useState<string>("");
  const [showAddAdminSuccess, setShowAddAdminSuccess] =
    React.useState<boolean>(false);
  const [addingAdmin, setAddingAdmin] = React.useState<boolean>(false);
  const [showSetOwnerSuccess, setShowSetOwnerSuccess] = React.useState<boolean>(false);
  const [settingOwner, setSettingOwner] = React.useState<boolean>(false);

  const { dataLeagues } = useLeagues({
    NameFilter: leaguesFilter,
    CountryCode: "",
    page: "0",
    pageSize: "99",
  });
  const handleLeagueChange = (
    event: React.ChangeEvent<unknown>,
    value: any
  ) => {
    formik.setFieldValue("leagueId", value.id);
  };

  const handleAddAdmin = async (user: IUsers) => {
    if (!organizationId) return;

    setAddingAdmin(true);
    try {
      await addAdminToOrganization(organizationId, user.id);
      setShowAddAdminSuccess(true);
      setTimeout(() => setShowAddAdminSuccess(false), 3000);
    } catch (error) {
      console.error("Error adding admin:", error);
      setShowSlideError(true);
      setTimeout(() => setShowSlideError(false), 3000);
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleSetOwner = async (user: IUsers) => {
    if (!organizationId) return;
    
    setSettingOwner(true);
    try {
      await setOrganizationOwner(organizationId, user.id);
      setShowSetOwnerSuccess(true);
      setTimeout(() => setShowSetOwnerSuccess(false), 3000);
    } catch (error) {
      console.error("Error setting owner:", error);
      setShowSlideError(true);
      setTimeout(() => setShowSlideError(false), 3000);
    } finally {
      setSettingOwner(false);
    }
  };

  const handleAddressSelect = (address: ParsedAddress) => {
    formik.setFieldValue("address.line1", address.line1);
    formik.setFieldValue("address.line2", address.line2);
    formik.setFieldValue("address.zipCode", address.zipCode);
    formik.setFieldValue("address.city", address.city);
    formik.setFieldValue("address.region", address.region);
    // Set country code for the organization
    if (address.countryCode) {
      formik.setFieldValue("countryCode", address.countryCode);
    }
  };

  const formik = useFormik({
    initialValues: initOrganization,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      console.log(values);
      if (acceptedFiles && acceptedFiles?.length > 0) {
        setIsLoading(true);
        postOrganizationImage(acceptedFiles)
          .then((res) => {
            putOrganization({
              ...values,
              logoUrl: res.fileUrl,
              logoName: res.fileName,
            })
              .then((res) => {
                setIsLoading(false);
                setNewOrganization(dataOrganization?.id);
              })
              .catch((e: any) => {
                setShowSlideError(true);
                setIsLoading(false);
              });
          })
          .catch((e: any) => {
            setShowSlideError(true);
            setIsLoading(false);
          });
      } else {
        setIsLoading(true);
        putOrganization({
          ...values,
          logoName: dataOrganization?.logoUrl
            ? dataOrganization?.logoUrl
                .split("/")
                ?.pop()
                ?.split("#")[0]
                .split("?")[0]
            : "",
        })
          .then((res) => {
            setIsLoading(false);
            setNewOrganization(dataOrganization?.id);
          })
          .catch((e: any) => {
            setShowSlideError(true);
            setIsLoading(false);
          });
      }
    },
  });

  return (
    <>
      <Typography
        component="h1"
        variant="h1"
        color="inherit"
        noWrap
        gutterBottom
        sx={{
          textAlign: "center",
          margin: "40px 0px",
        }}
      >
        Edit Organization
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack
          spacing={3}
          sx={{
            maxWidth: "500px",
            margin: "auto",
            opacity: newOrganization || isLoading ? 0.4 : 1,
            pointerEvents: isLoading ? "none" : "auto",
          }}
        >
          <img
            src={dataOrganization?.logoUrl}
            style={{ width: "auto", height: 124, margin: "auto" }}
          />
          <UploadImage
            maxFiles={1}
            onAcceptedFilesChange={(files: File[]) => {
              setAcceptedFiles(files);
            }}
          />
          <TextField
            fullWidth
            id="id"
            name="id"
            label="id"
            type="text"
            disabled={true}
            value={formik.values.id}
            onChange={formik.handleChange}
            error={formik.touched.id && Boolean(formik.errors.id)}
            helperText={formik.touched.id && formik.errors.id}
          />
          <TextField
            fullWidth
            id="name"
            name="name"
            label="Name"
            type="text"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            fullWidth
            id="shortName"
            name="shortName"
            label="Short name"
            type="text"
            value={formik.values.shortName}
            onChange={formik.handleChange}
            error={formik.touched.shortName && Boolean(formik.errors.shortName)}
            helperText={formik.touched.shortName && formik.errors.shortName}
          />

          {dataLeagues && (
            <>
              <AutocompleteDebounce
                handleChange={handleLeagueChange}
                data={dataLeagues.leagues}
                setDefaultValueWithId={formik.values.leagueId}
                onFilterChange={(value) => setLeaguesFilter(value)}
                labelTitle="Select a league"
                formikErrors={formik.errors.leagueId}
                formikTouched={formik.touched.leagueId}
              />
            </>
          )}
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label" shrink={true}>
              Sport Type
            </InputLabel>
            <Select
              labelId="sportType"
              id="sportType"
              name="sportType"
              value={formik.values.sportType}
              label="sportType"
              onChange={formik.handleChange}
            >
              <MenuItem value="Football">Football</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label" shrink={true}>
              Type
            </InputLabel>
            <Select
              labelId="type"
              id="type"
              name="type"
              value={formik.values.type}
              label="sportType"
              onChange={formik.handleChange}
            >
              <MenuItem value={OrganizationEnum.Default}>
                {OrganizationEnum.Default}
              </MenuItem>
              <MenuItem value={OrganizationEnum.Club}>
                {OrganizationEnum.Club}
              </MenuItem>
              <MenuItem value={OrganizationEnum.HighSchool}>
                {OrganizationEnum.HighSchool}
              </MenuItem>
              <MenuItem value={OrganizationEnum.University}>
                {OrganizationEnum.University}
              </MenuItem>
              <MenuItem value={OrganizationEnum.College}>
                {OrganizationEnum.College}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label" shrink={true}>
              Country code
            </InputLabel>

            <Select
              labelId="countryCode"
              id="countryCode"
              name="countryCode"
              value={formik.values.countryCode}
              label="countryCode"
              onChange={formik.handleChange}
            >
              {getCountries().map((country: any) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name} ({country.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Admin Management Section */}
          <Typography variant="h4" sx={{ mt: 3, mb: 2 }}>
            Admin Management
          </Typography>
          <AddOrganizationAdmin onAdminSelected={handleAddAdmin} />
          {showAddAdminSuccess && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Admin added successfully!
            </Typography>
          )}
          {addingAdmin && <CircularProgress size={20} sx={{ mt: 1 }} />}

          {/* Owner Management Section */}
          <Typography variant="h4" sx={{ mt: 3, mb: 2 }}>
            Owner Management
          </Typography>
          <AddOrganizationOwner 
            onOwnerSelected={handleSetOwner}
          />
          {showSetOwnerSuccess && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Owner set successfully!
            </Typography>
          )}
          {settingOwner && <CircularProgress size={20} sx={{ mt: 1 }} />}

          <Typography component="h2" variant="h4">
            Address
          </Typography>
          <GooglePlacesAutocomplete
            onAddressSelect={handleAddressSelect}
            label="Search for address"
            initialCoordinates={
              dataOrganization?.address?.coordinates
                ? {
                    lat: dataOrganization.address.coordinates.lat,
                    lng: dataOrganization.address.coordinates.lon,
                  }
                : undefined
            }
          />
          <TextField
            fullWidth
            id="addressLine1"
            name="address.line1"
            label="Addres line 1"
            type="text"
            value={formik.values.address?.line1}
            onChange={formik.handleChange}
            error={
              formik.touched.address?.line1 &&
              Boolean(formik.errors.address?.line1)
            }
            helperText={
              formik.touched.address?.line1 && formik.errors.address?.line1
            }
          />
          <TextField
            fullWidth
            id="addressLine2"
            name="address.line2"
            label="Addres line 2"
            type="text"
            value={formik.values.address?.line2}
            onChange={formik.handleChange}
            error={
              formik.touched.address?.line2 &&
              Boolean(formik.errors.address?.line2)
            }
            helperText={
              formik.touched.address?.line2 && formik.errors.address?.line2
            }
          />
          <TextField
            fullWidth
            id="addressZipCode"
            name="address.zipCode"
            label="Addres zipCode"
            type="text"
            value={formik.values.address.zipCode}
            onChange={formik.handleChange}
            error={
              formik.touched.address?.zipCode &&
              Boolean(formik.errors.address?.zipCode)
            }
            helperText={
              formik.touched.address?.zipCode && formik.errors.address?.zipCode
            }
          />
          <TextField
            fullWidth
            id="addressCity"
            name="address.city"
            label="Addres city"
            type="text"
            value={formik.values.address.city}
            onChange={formik.handleChange}
            error={
              formik.touched.address?.city &&
              Boolean(formik.errors.address?.city)
            }
            helperText={
              formik.touched.address?.city && formik.errors.address?.city
            }
          />
          <TextField
            fullWidth
            id="addressRegion"
            name="address.region"
            label="Addres region"
            type="text"
            value={formik.values.address.region}
            onChange={formik.handleChange}
            error={
              formik.touched.address?.region &&
              Boolean(formik.errors.address?.region)
            }
            helperText={
              formik.touched.address?.region && formik.errors.address?.region
            }
          />
          <FormControlLabel
            sx={{
              margin: " 20px auto",
            }}
            control={
              <Switch
                checked={formik.values.isGhost}
                inputProps={{ "aria-label": "controlled" }}
                value={formik.values.isGhost}
                onChange={(e) => {
                  e.target.checked
                    ? formik.setFieldValue("isGhost", true)
                    : formik.setFieldValue("isGhost", false);
                }}
              />
            }
            label="Organization is a ghost organization?"
          />

          <Button
            color="secondary"
            variant="contained"
            fullWidth
            type="submit"
            endIcon={
              isLoading && (
                <IconButton aria-label="" edge="end">
                  <CircularProgress
                    size={15}
                    sx={{ color: "#68F3CB" }}
                    color="success"
                  />
                </IconButton>
              )
            }
          >
            Update Organization
          </Button>
          <ErrorMessageSlide showError={showSlideError} />
        </Stack>

        <Dialog
          open={!!newOrganization}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => {
            setNewOrganization(undefined);
          }}
          aria-describedby="alert-dialog-slide-description"
          PaperProps={{
            sx: { backgroundColor: "#fff", minWidth: "400px" },
          }}
        >
          <DialogTitle variant="h4">
            {"Organization successfully updated!"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description"></DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                setNewOrganization(undefined);
              }}
            >
              close
            </Button>
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={() => navigate(`/organizations/${dataOrganization?.id}`)}
            >
              Go to Organization page
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </>
  );
}
